import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Trash2, Plus, Square } from 'lucide-react';
import { flexZonePeople } from '../../lib/arenaUtils';

// 1 méter = 10 SVG egység (egyszerű skálázás)
export const M_TO_PX = 10;

export default function FlexZonePanel({
  flexZones,
  setFlexZones,
  selectedFlexId,
  setSelectedFlexId,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: '', widthM: 20, heightM: 10, peoplePerSqm: 4 });

  const addZone = () => {
    if (!draft.name) return;
    const w = parseFloat(draft.widthM) || 0;
    const h = parseFloat(draft.heightM) || 0;
    const p = parseFloat(draft.peoplePerSqm) || 4;
    const id = `flex-${Date.now()}`;
    setFlexZones((prev) => [
      ...prev,
      {
        id,
        name: draft.name,
        widthM: w,
        heightM: h,
        peoplePerSqm: p,
        widthPx: w * M_TO_PX,
        heightPx: h * M_TO_PX,
        x: 800,
        y: 700,
      },
    ]);
    setSelectedFlexId(id);
    setDraft({ name: '', widthM: 20, heightM: 10, peoplePerSqm: 4 });
    setOpen(false);
  };

  // A méter input MOST független a térképen rajzolt mérettől - csak a kapacitást befolyásolja.
  const updateZone = (id, patch) => {
    setFlexZones((prev) =>
      prev.map((z) => (z.id !== id ? z : { ...z, ...patch }))
    );
  };

  const removeZone = (id) => {
    setFlexZones((prev) => prev.filter((z) => z.id !== id));
    if (selectedFlexId === id) setSelectedFlexId(null);
  };

  const totalFlex = flexZones.reduce((acc, z) => acc + flexZonePeople(z), 0);

  return (
    <Card className="p-4 bg-white border-stone-200" data-testid="flex-zones-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold tracking-wide text-stone-800 uppercase">Flex zónák</h3>
          <p className="text-xs text-stone-500 mt-0.5">{totalFlex} fő összesen</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="default" data-testid="add-flex-zone-btn" className="h-8 px-3">
              <Plus className="w-3.5 h-3.5 mr-1" /> Új zóna
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-flex-dialog">
            <DialogHeader>
              <DialogTitle>Flex zóna hozzáadása</DialogTitle>
              <DialogDescription>Add meg a flex zóna nevét és méretét. A kapacitás m² × fő/m² alapján számolódik.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs">Név</Label>
                <Input
                  data-testid="flex-name-input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="pl. Belső placc"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Szélesség (m)</Label>
                  <Input
                    data-testid="flex-width-input"
                    type="number"
                    min="1"
                    value={draft.widthM}
                    onChange={(e) => setDraft({ ...draft, widthM: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Magasság (m)</Label>
                  <Input
                    data-testid="flex-height-input"
                    type="number"
                    min="1"
                    value={draft.heightM}
                    onChange={(e) => setDraft({ ...draft, heightM: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fő / m²</Label>
                <Input
                  data-testid="flex-people-input"
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={draft.peoplePerSqm}
                  onChange={(e) => setDraft({ ...draft, peoplePerSqm: e.target.value })}
                />
              </div>
              <p className="text-sm text-stone-600">
                Várható kapacitás:&nbsp;
                <span className="font-bold">
                  {Math.floor((parseFloat(draft.widthM) || 0) * (parseFloat(draft.heightM) || 0) * (parseFloat(draft.peoplePerSqm) || 0))} fő
                </span>
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Mégse</Button>
              <Button onClick={addZone} data-testid="save-flex-zone-btn">Hozzáadás</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {flexZones.length === 0 ? (
        <div className="text-xs text-stone-500 text-center py-6 border border-dashed border-stone-300 rounded-md">
          <Square className="w-5 h-5 mx-auto mb-1.5 text-stone-400" />
          Nincs még flex zóna.
        </div>
      ) : (
        <div className="space-y-2">
          {flexZones.map((z) => (
            <div
              key={z.id}
              className={`p-2.5 rounded-md border transition-colors ${
                selectedFlexId === z.id ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:bg-stone-50'
              }`}
              onClick={() => setSelectedFlexId(z.id)}
              data-testid={`flex-zone-card-${z.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-stone-800">{z.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeZone(z.id); }}
                  className="text-stone-400 hover:text-red-600 transition-colors"
                  data-testid={`remove-flex-${z.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                <div>
                  <Label className="text-[10px] text-stone-500">szél (m)</Label>
                  <Input
                    type="number"
                    className="h-7 text-xs"
                    value={z.widthM}
                    onChange={(e) => updateZone(z.id, { widthM: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-stone-500">mag (m)</Label>
                  <Input
                    type="number"
                    className="h-7 text-xs"
                    value={z.heightM}
                    onChange={(e) => updateZone(z.id, { heightM: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-stone-500">fő/m²</Label>
                  <Input
                    type="number"
                    step="0.5"
                    className="h-7 text-xs"
                    value={z.peoplePerSqm}
                    onChange={(e) => updateZone(z.id, { peoplePerSqm: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="mt-2 text-xs text-emerald-700 font-semibold">
                = {flexZonePeople(z)} fő ({(z.widthM * z.heightM).toFixed(1)} m²)
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
